/**
 * Filter chips for a release page's table of contents (spec-feature.md §3.3
 * 目次).
 *
 * internal/site/templates/releases.templ's ReleaseContent renders a
 * `.release-toc` card containing:
 *   - `.release-toc-filters` - a row of filter chips (`.release-toc-filter-chip`,
 *     each with a `data-toc-filter` value of "all" or one of the six
 *     annotate.ChangeKind strings), rendered `hidden` server-side so it only
 *     appears once this module has run (progressive enhancement: with no
 *     JS, every entry stays visible and unfiltered).
 *   - `.release-toc-list` - the scrollable entry list itself, one
 *     `.release-toc-entry--section` `<li>` per heading (always shown, even
 *     with zero nested items - the TOC doubles as a document-structure
 *     map), each optionally containing `.release-toc-entry--item` `<li>`s
 *     for its own annotated items, carrying every one of that item's change
 *     kinds space-joined in `data-change-kinds`.
 *
 * A chip's selected/unselected look is driven entirely by its own literal
 * `aria-[pressed=true]:...` UnoCSS utility classes already present in the
 * server-rendered markup (releases.templ) - this module only ever flips the
 * `aria-pressed` attribute, never touches className, so there is no
 * class-name vocabulary to keep in sync between Go and TS here (unlike
 * copy-link.ts's VARIANT_CLASS, which does need that because it *builds*
 * classes client-side).
 *
 * Selection is a single-select toggle: choosing a kind chip shows only item
 * entries whose data-change-kinds includes that kind, and hides any section
 * entry left with no visible item entries; clicking "すべて", or
 * re-clicking the already-selected chip, clears the filter and restores
 * every entry (spec-feature.md's 目次 §フィルタ: "選択中チップ再クリックか
 * 「すべて」で解除"). The filter only ever hides/shows TOC entries - it
 * never touches the release page's own body content.
 */

const TOC_SELECTOR = '.release-toc';
const FILTERS_SELECTOR = '.release-toc-filters';
const CHIP_SELECTOR = '.release-toc-filter-chip';
const SECTION_ENTRY_SELECTOR = '.release-toc-entry--section';
const ITEM_ENTRY_SELECTOR = '.release-toc-entry--item';

const ALL_FILTER = 'all';

/** Reads the space-separated data-change-kinds attribute into a token array. */
function changeKindsOf(item: HTMLElement): string[] {
  return (item.dataset.changeKinds ?? '').split(' ').filter(Boolean);
}

/**
 * Applies filter (a data-toc-filter value, or ALL_FILTER) to toc: updates
 * every chip's aria-pressed, shows/hides item entries by whether they carry
 * the selected kind, and hides section entries whose whole subtree has no
 * visible item once a kind filter is active. Section entries are flat
 * siblings whose hierarchy lives in data-toc-level (releases.templ), so a
 * section's subtree is the run of following sections with a deeper level;
 * judging visibility over that run keeps every ancestor heading of a
 * matching item visible as context. Exported for testing.
 */
export function applyTOCFilter(toc: HTMLElement, chips: HTMLButtonElement[], filter: string): void {
  chips.forEach((chip) => {
    const chipFilter = chip.dataset.tocFilter ?? ALL_FILTER;
    chip.setAttribute('aria-pressed', String(chipFilter === filter));
  });

  const showAll = filter === ALL_FILTER;

  toc.querySelectorAll<HTMLElement>(ITEM_ENTRY_SELECTOR).forEach((item) => {
    item.hidden = !showAll && !changeKindsOf(item).includes(filter);
  });

  const sections = [...toc.querySelectorAll<HTMLElement>(SECTION_ENTRY_SELECTOR)];
  const hasOwnVisibleItem = sections.map(
    (section) => section.querySelector<HTMLElement>(`${ITEM_ENTRY_SELECTOR}:not([hidden])`) !== null,
  );
  const levelOf = (section: HTMLElement): number => {
    const level = Number(section.dataset.tocLevel);
    return Number.isFinite(level) ? level : 0;
  };

  sections.forEach((section, i) => {
    if (showAll) {
      section.hidden = false;
      return;
    }
    let visible = hasOwnVisibleItem[i];
    for (let j = i + 1; !visible && j < sections.length && levelOf(sections[j]) > levelOf(section); j++) {
      visible = hasOwnVisibleItem[j];
    }
    section.hidden = !visible;
  });
}

/** Marks a `.release-toc-filters` row whose chips already have listeners bound. */
const INITIALIZED_ATTR = 'data-toc-initialized';

/**
 * Wires up filter-chip click handling for every `.release-toc` on the page,
 * and reveals the (server-rendered `hidden`) filter row now that JS is
 * available to drive it. Safe to call multiple times: a filter row already
 * marked with INITIALIZED_ATTR is skipped entirely, so chip listeners are
 * never bound twice. Exported for testing.
 */
export function initializeReleaseTOC(): void {
  document.querySelectorAll<HTMLElement>(TOC_SELECTOR).forEach((toc) => {
    const filters = toc.querySelector<HTMLElement>(FILTERS_SELECTOR);
    if (!filters) return; // No annotated items on this release: nothing to filter.

    filters.hidden = false;
    if (filters.hasAttribute(INITIALIZED_ATTR)) return;
    filters.setAttribute(INITIALIZED_ATTR, 'true');

    const chips = Array.from(filters.querySelectorAll<HTMLButtonElement>(CHIP_SELECTOR));
    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const filter = chip.dataset.tocFilter ?? ALL_FILTER;
        const alreadySelected = chip.getAttribute('aria-pressed') === 'true';
        const next = filter !== ALL_FILTER && alreadySelected ? ALL_FILTER : filter;
        applyTOCFilter(toc, chips, next);
      });
    });
  });
}
